<script setup lang="ts">
import Dropdown from "primevue/dropdown";
import InputNumber from "primevue/inputnumber";
import Textarea from "primevue/textarea";
import type { Ref } from "vue";
import { convertCost } from "@/components/program-manager/sessions/utils";
import { MAX_NOTE_TEXTAREA_LIMIT } from "@/constants";
import { centsToDollars } from "@/mixins/helpers";
import type { ClassesFeeModalData, Reason } from "../types";

defineOptions({
  name: "AcademicFepriceOverrideSectionsModalUiV2",
});

interface Props {
  dropdown: string | null;
  textarea: string | null;
  newprice: number | null;
  reasons: Reason[];
  error: boolean;
  errorMessage?: string | Ref<string>;
  inputId?: string;
  disabled?: boolean;
  otherVisible: boolean;
  textareaError: boolean;
  textareaErrorMessage?: string | Ref<string>;
  modalData: ClassesFeeModalData;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  (e: "update:dropdown", value: string | null): void;
  (e: "update:textarea", value: string | null): void;
  (e: "update:newprice", value: number | null): void;
}>();
</script>

<template>
  <div>
    <div class="w-full font-montserrat">
      <!-- Current + new price -->
      <div class="flex flex-col md:flex-row items-start md:items-center justify-between mb-6 pr-2 pl-2">
        <div class="text-sm font-semibold">
          Current Product Price:
          <span class="font-normal"
            >{{ `${convertCost(centsToDollars(modalData?.total_price_in_cents))}` }}
          </span>
        </div>

        <div class="flex items-center">
          <label class="text-sm font-semibold whitespace-nowrap pr-2">New Price $</label>
          <InputNumber
            :model-value="newprice"
            @update:model-value="emit('update:newprice', $event)"
            :minFractionDigits="2"
            :maxFractionDigits="2"
            :min="0"
            class="flex-1 rounded h-10"
          />
        </div>
      </div>

      <!-- Override reason -->
      <div class="flex flex-col md:flex-row items-center mb-6 pr-2 pl-2">
        <label class="text-sm font-semibold md:w-40 shrink-0">Override Reason</label>

        <Dropdown
          :model-value="dropdown"
          @update:model-value="emit('update:dropdown', $event)"
          :options="reasons"
          optionLabel="name"
          optionValue="id"
          :id="inputId"
          :disabled="disabled"
          class="md:w-full flex-1"
          :invalid="error"
          placeholder="Select a reason"
        >
          <template #option="optionSlotProps">
            <p>
              {{ optionSlotProps.option.name }}
            </p>
            <p class="ml-4 mt-2 text-gray-600 text-sm">
              {{ optionSlotProps.option.description }}
            </p>
          </template>
        </Dropdown>
      </div>

      <!-- Notes -->
      <div class="flex flex-col md:flex-row items-center mb-6 pr-2 pl-2">
        <label class="text-sm font-semibold md:w-40 shrink-0">Additional Notes</label>
        <div class="flex flex-col flex-1 items-end">
          <Textarea
            :model-value="textarea"
            class="w-full"
            :disabled="disabled"
            :invalid="textareaError"
            :maxlength="MAX_NOTE_TEXTAREA_LIMIT"
            @update:model-value="emit('update:textarea', $event)"
            autoResize
          />
          <!-- Counter -->
          <div class="text-sm text-gray-500">{{ textarea?.length }} / {{ MAX_NOTE_TEXTAREA_LIMIT }}</div>
        </div>
      </div>
    </div>
  </div>
</template>
<style scoped>
:deep([data-pc-name="input"][data-pc-extend="inputtext"][data-pc-section="root"]) {
  border-radius: 0.25rem !important;
}
</style>
